// The loyalty card — GET customer/me/loyalty-card. Each order banks a
// reward on the open card; at the program's count the card is ready and
// the checkout redeems it whole (the redeemLoyalty flag — never a discount
// line). No card means no program, or no qualifying order yet: the API
// does not say which.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { fetchMyCard, money, type LoyaltyCard } from '../../lib/api';
import { AccountHead, accountPath } from './shared';

export function Loyalty() {
  const { rid, sid } = useParams() as { rid: string; sid: string };
  const [card, setCard] = useState<LoyaltyCard | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMyCard(rid).then(setCard, (e) => setError(e instanceof Error ? e.message : 'Could not load your card.'));
  }, [rid]);

  return (
    <div className="co-page">
      <AccountHead back={accountPath(rid, sid)} backLabel="Back to your account" kicker="Your account" title="Loyalty card" />
      {error && <div className="co-err acct-err" role="alert">{error}</div>}
      {card === undefined && !error && <div className="loading">Looking…</div>}
      {card === null && (
        <section className="co-sec" data-section="card" data-card="none">
          <p className="acct-quiet">No card yet. If the restaurant runs a loyalty program, your next order starts one.</p>
        </section>
      )}
      {card && (
        <>
          <section className={'acct-card' + (card.isRedeemable ? ' ready' : '')} data-section="card" data-card={card.cardId} data-stamps={card.rewards.length} data-value={card.valueInCents} data-redeemable={card.isRedeemable}>
            <div className="acct-card-k">Banked</div>
            <div className="acct-card-v money">{money(card.valueInCents)}</div>
            <div className="acct-stamps" aria-label={`${card.rewards.length} of ${card.ordersPerCard} orders`}>
              {Array.from({ length: card.ordersPerCard }, (_, i) => <span key={i} className={'acct-stamp' + (i < card.rewards.length ? ' on' : '')} />)}
            </div>
            <div className="acct-card-note">
              {card.isRedeemable
                ? `Ready — take ${money(card.valueInCents)} off your next order at checkout.`
                : `${card.ordersPerCard - card.rewards.length} more ${card.ordersPerCard - card.rewards.length === 1 ? 'order' : 'orders'} and it's yours to spend.`}
            </div>
          </section>
          {card.isRedeemable && (
            <div className="acct-pad"><Link to={`/r/${rid}/s/${sid}`} className="co-back">Order now →</Link></div>
          )}
          {card.rewards.length > 0 && (
            <section className="co-sec" data-section="rewards">
              <h2>On this card</h2>
              {card.rewards.map((r) => (
                <div className="co-line" key={r.orderId} data-reward={r.orderId}>
                  <span className="nm">{new Date(r.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                    <span className="cfg">order of {money(r.orderGrandTotalInCents)}</span>
                  </span>
                  <span className="amt money">+ {money(r.amountInCents)}</span>
                </div>
              ))}
            </section>
          )}
        </>
      )}
    </div>
  );
}
